import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import type { CircleAiConversation } from "@circle-react/api/circleAiConversationsApi";
import { circleAiConversationsApi } from "@circle-react/api/circleAiConversationsApi";
import { CIRCLE_AI_CONVERSATIONS_QUERY_KEY } from "@circle-react/components/CircleAi/Home/RecentChatsDrawer/useRecentChatsList";
import { useCircleAiStore } from "@circle-react/components/CircleAi/useCircleAiStore";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useConversationActionsToast } from "./useConversationActionsToast";

type DeletableConversation = Pick<CircleAiConversation, "uuid" | "title">;

interface UseDeleteConversationOptions {
  errorMessage: string;
  successMessage: string;
}

export const useDeleteConversation = ({
  errorMessage,
  successMessage,
}: UseDeleteConversationOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { success, error } = useConversationActionsToast();
  const [conversationPendingDeletion, setConversationPendingDeletion] =
    useState<DeletableConversation | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ uuid }: { uuid: string }) =>
      circleAiConversationsApi.destroy({ id: uuid }),
    onSuccess: (_data, { uuid }) => {
      void queryClient.invalidateQueries({
        queryKey: [CIRCLE_AI_CONVERSATIONS_QUERY_KEY],
      });

      // The DELETE is async, so read live store/route state rather than the
      // render-time snapshot — the active chat or route may have changed while
      // the request was in flight. The reset (active chat) and the route
      // cleanup (URL chat) are independent: page and panel can host different
      // conversations.
      const circleAiStore = useCircleAiStore.getState();
      const isActiveConversation = uuid === circleAiStore.activeConversation.id;
      const conversationRoute = matchRoute({ to: "/ai/$conversationId" });

      if (isActiveConversation) {
        circleAiStore.startNewChat({ openPanel: false });
      }
      if (conversationRoute && conversationRoute.conversationId === uuid) {
        void navigate({ to: "/ai" });
      } else if (isActiveConversation) {
        const shellStore = useCircleAIShellStore.getState();
        shellStore.setConversationPanelState("closed");
        shellStore.setAdminDockForced(false);
      }

      success(successMessage);
    },
    onError: () => {
      error(errorMessage);
    },
  });

  const requestDelete = useCallback(
    (conversation: DeletableConversation) =>
      setConversationPendingDeletion({
        uuid: conversation.uuid,
        title: conversation.title,
      }),
    [],
  );

  const cancelDelete = useCallback(() => {
    if (isPending) return;
    setConversationPendingDeletion(null);
  }, [isPending]);

  const confirmDelete = useCallback(() => {
    if (!conversationPendingDeletion) return;

    mutate(
      { uuid: conversationPendingDeletion.uuid },
      { onSuccess: () => setConversationPendingDeletion(null) },
    );
  }, [conversationPendingDeletion, mutate]);

  return {
    conversationPendingDeletion,
    isDeleting: isPending,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
};
